import React, { useEffect, useState } from "react";
import { Bell, X, AlertOctagon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const { formatDateTime } = useLocale();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const eligible = user && ["host", "site_manager", "org_admin", "super_admin", "receptionist"].includes(user.role);

  const load = async () => {
    if (!eligible) return;
    try {
      const { data } = await api.get("/notifications/in-app", { params: { status: "unread", limit: 10 } });
      setItems(data.items || []);
      setUnread(data.unread_count ?? 0);
    } catch { /* not authed yet */ }
  };

  useEffect(() => {
    if (!eligible) return;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  const markRead = async (notifId) => {
    try {
      await api.post(`/notifications/in-app/${notifId}/mark-read`);
      load();
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/in-app/mark-all-read");
      load();
    } catch { /* ignore */ }
  };

  if (!eligible) return null;

  const hasWatchlist = items.some((n) => n.type === "watchlist_hit");

  return (
    <div className="relative" data-testid="notification-bell-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-700 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full text-white border-2 border-white ${
              hasWatchlist ? "bg-danger-600 animate-pulse" : "bg-orange-500"
            }`}
            data-testid={hasWatchlist ? "notification-bell-watchlist" : "notification-bell-count"}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[460px] bg-white rounded-xl border border-navy-100 shadow-xl z-50 flex flex-col"
          data-testid="notification-bell-popover"
        >
          <div className="px-4 py-3 border-b border-navy-100 flex items-center justify-between">
            <div className="font-semibold text-sm">Notifications</div>
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-orange-600 hover:underline"
                  data-testid="notif-mark-all-read"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-navy-400 hover:text-navy-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-navy-500">No new notifications.</div>
            ) : (
              items.map((n) => {
                const p = n.payload || {};
                if (n.type === "watchlist_hit") {
                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        markRead(n.id);
                        setOpen(false);
                        navigate(`/admin/watchlist-hits/${p.watchlist_hit_id}`);
                      }}
                      className="w-full text-left px-4 py-3 border-b border-navy-50 bg-danger-50/40 hover:bg-danger-50 transition-colors"
                      data-testid={`notif-watchlist-${n.id}`}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-danger-700">
                        <AlertOctagon className="w-4 h-4" /> Watchlist match — see reception
                      </div>
                      <div className="text-xs text-navy-700 mt-1">
                        First name: {p.attempted_visitor_first_name || "—"} · entry: {p.watchlist_entry_name_initial || "?"}.
                      </div>
                      <div className="text-[10px] text-navy-400 mt-0.5">
                        {n.created_at ? formatDateTime(n.created_at) : ""}
                      </div>
                    </button>
                  );
                }
                // Phase B3 — support ticket updates (both customer + super_admin sides).
                if (n.type === "support.ticket_new" || n.type === "support.ticket_update") {
                  const isSuperSide = n.type === "support.ticket_new";
                  const target = isSuperSide
                    ? `/super-admin/tickets/${p.ticket_id}`
                    : `/help/tickets/${p.ticket_id}`;
                  return (
                    <button
                      key={n.id}
                      onClick={() => { markRead(n.id); setOpen(false); navigate(target); }}
                      className="w-full text-left px-4 py-3 border-b border-navy-50 hover:bg-navy-50/50"
                      data-testid={`notif-support-${n.id}`}
                    >
                      <div className="text-sm text-navy-900">
                        <strong>{isSuperSide ? "New ticket" : "Support replied"}</strong>
                        {" — "}{p.subject || "Untitled ticket"}
                      </div>
                      {p.org_name && (
                        <div className="text-[11px] text-navy-500 mt-0.5">From {p.org_name}</div>
                      )}
                      <div className="text-[10px] text-navy-400 mt-0.5">
                        {n.created_at ? formatDateTime(n.created_at) : ""}
                      </div>
                    </button>
                  );
                }
                return (
                  <div
                    key={n.id}
                    className="px-4 py-3 border-b border-navy-50 hover:bg-navy-50/50 cursor-pointer"
                    onClick={() => markRead(n.id)}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="text-sm text-navy-900">
                      <strong>{p.visitor_name}</strong>
                      {p.visitor_company ? ` (${p.visitor_company})` : ""} signed in
                      {p.site_name ? ` at ${p.site_name}` : ""}
                    </div>
                    {p.deviation_tag && (
                      <div className="text-[11px] text-orange-600 mt-0.5">{p.deviation_tag}</div>
                    )}
                    <div className="text-[10px] text-navy-400 mt-0.5">
                      {n.created_at ? formatDateTime(n.created_at) : ""}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="px-4 py-2 border-t border-navy-100 text-center">
            <Link
              to="/notifications"
              className="text-xs text-orange-600 hover:underline"
              onClick={() => setOpen(false)}
              data-testid="notif-view-all"
            >
              View all →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
